<template>
  <Layout>
    <PageHeader :title="$t('module.userDetails')" :items="breadcrumbs" />
    <div class="d-flex justify-content-end mb-3">
      <b-button
        v-if="user"
        variant="info"
        class="mr-2"
        :disabled="loading || editSaving"
        @click="openEdit"
        ><i class="mdi mdi-pencil-outline mr-1"></i
        >{{ $t("label.updateX", [$t("module.user")]) }}</b-button
      ><b-button :to="{ name: 'User' }" variant="outline-secondary"
        ><i class="mdi mdi-arrow-left mr-1"></i
        >{{ $t("label.userDetailsBack") }}</b-button
      >
    </div>
    <div v-if="loading" class="text-center p-5">
      <b-spinner variant="info" />
    </div>
    <b-alert v-else-if="error" show variant="danger"
      >{{ error }}
      <b-button size="sm" variant="outline-danger" @click="loadUser">{{
        $t("label.retryPermissionDefinitions")
      }}</b-button></b-alert
    >
    <template v-else-if="user">
      <div class="row">
        <div class="col-12 col-lg-4">
          <div class="card">
            <div class="card-body text-center">
              <img
                v-if="user.image && !imageError"
                :src="user.image"
                :alt="user.name"
                class="user-detail-avatar rounded-circle mb-3"
                @error="imageError = true"
              />
              <div
                v-else
                class="user-detail-avatar user-detail-initials rounded-circle bg-soft-info text-info mx-auto mb-3"
              >
                {{ user.initials }}
              </div>
              <h4 class="text-break">{{ user.name }}</h4>
              <p class="text-muted text-break">{{ user.email }}</p>
              <b-badge
                :variant="user.isActive ? 'success' : 'secondary'"
                class="mr-1"
                >{{
                  $t(user.isActive ? "label.active" : "label.inactive")
                }}</b-badge
              >
              <b-badge
                v-if="[1, '1'].includes(user.userType)"
                variant="info"
                class="mr-1"
                >{{ $t("label.userDetailsExternalUser") }}</b-badge
              >
              <b-badge v-if="user.isSuperadmin" class="user-superadmin">{{
                $t("label.userDetailsSuperAdmin")
              }}</b-badge>
              <hr />
              <dl class="text-left mb-0">
                <div v-for="item in summary" :key="item.label" class="mb-3">
                  <dt class="small text-muted">{{ $t(item.label) }}</dt>
                  <dd class="mb-0 text-break">{{ item.value }}</dd>
                </div>
              </dl>
            </div>
          </div>
        </div>
        <div class="col-12 col-lg-8">
          <div class="card">
            <div class="card-body">
              <h5 class="mb-4">{{ $t("label.userDetailsPersonal") }}</h5>
              <dl class="row mb-0">
                <div
                  v-for="item in personal"
                  :key="item.label"
                  class="col-12 col-md-6 mb-3"
                >
                  <dt class="small text-muted">{{ $t(item.label) }}</dt>
                  <dd class="mb-0 text-break">{{ item.value }}</dd>
                </div>
              </dl>
            </div>
          </div>
          <div class="card">
            <div class="card-body">
              <h5 class="mb-3">{{ $t("label.userDetailsAccess") }}</h5>
              <b-table :items="accessRows" :fields="accessFields" responsive>
                <template #cell(access)="{ item }"
                  ><b-badge
                    :variant="
                      item.access === 1
                        ? 'success'
                        : item.access === 2
                        ? 'warning'
                        : 'secondary'
                    "
                    >{{ accessLabel(item.access) }}</b-badge
                  ></template
                >
              </b-table>
            </div>
          </div>
        </div>
      </div>
      <div class="card">
        <div class="card-body">
          <h5 class="mb-3">{{ $t("label.userDetailsCompanies") }}</h5>
          <b-table
            :items="user.companies"
            :fields="companyFields"
            primary-key="uuid"
            responsive
            show-empty
          ><template #cell(company)="{ item }"><CompanyLink :uuid="item.companyUUID" :name="item.company" /></template></b-table>
        </div>
      </div>
      <div class="card">
        <div class="card-body">
          <h5 class="mb-3">
            {{ $t("label.userDetailsPermissions") }}
            <b-spinner v-if="definitionsLoading" small variant="info" />
          </h5>
          <p v-if="user.isSuperadmin" class="small text-muted">
            {{ $t("label.userDetailsSuperAdminHint") }}
          </p>
          <b-table
            :items="permissions"
            :fields="permissionFields"
            primary-key="key"
            responsive
            show-empty
          >
            <template #cell(level)="{ item }"
              ><b-badge
                :variant="
                  item.level === 2
                    ? 'success'
                    : item.level === 1
                    ? 'info'
                    : 'secondary'
                "
                >{{ item.level }} —
                {{
                  $t(
                    [
                      "label.userDetailsBlocked",
                      "label.userDetailsRead",
                      "label.userDetailsWrite",
                    ][item.level],
                  )
                }}</b-badge
              ></template
            >
            <template #cell(definition)="{ item }">{{
              item.definition || "-"
            }}</template>
          </b-table>
        </div>
      </div>
    </template>
    <b-modal
      id="user-details-edit"
      :title="$t('label.updateX', [$t('module.user')])"
      size="xl"
      hide-footer
      centered
      :no-close-on-backdrop="editSaving"
      :no-close-on-esc="editSaving"
      :hide-header-close="editSaving"
      @hidden="editVisible = false"
    >
      <UserForm
        v-if="editVisible"
        :key="userUUID"
        :user-u-u-i-d="userUUID"
        :search-criteria="{}"
        @done="closeEdit"
        @saved="loadUser"
      />
    </b-modal>
  </Layout>
</template>
<script>
import Layout from "@/views/layouts/main";
import PageHeader from "@/components/page-header";
import UserForm from "./userForm.vue";
import i18n from "@/i18n";
import { userService } from "@/services/api/user.service";
import {
  userDetailsModel,
  userDetailPermissions,
} from "@/models/api/userDetails.model";
import { userPermissionDefinitionsModel } from "@/models/api/userPermissionDefinitions.model";
import { sharedHelper } from "@/utils";
export default {
  name: "UserDetails",
  page: { title: i18n.t("module.userDetails") },
  components: { Layout, PageHeader, UserForm },
  props: { userUUID: { type: String, required: true } },
  data() {
    return {
      user: null,
      loading: true,
      error: "",
      definitions: [],
      definitionsLoading: false,
      requestId: 0,
      imageError: false,
      editVisible: false,
    };
  },
  computed: {
    editSaving() {
      return this.$store.state.api.user.status.save === false;
    },
    breadcrumbs() {
      return [
        { text: this.$t("module.administration"), href: "/administrations" },
        { text: this.$t("module.user"), href: "/users" },
        { text: this.$t("module.userDetails"), active: true },
      ];
    },
    summary() {
      return [
        { label: "label.userDetailsUuid", value: this.user.uuid },
        {
          label: "label.userDetailsOrientation",
          value: this.$t(this.user.isOrientation ? "label.yes" : "label.no"),
        },
      ];
    },
    personal() {
      return [
        { label: "label.userDetailsFirstName", value: this.user.firstName },
        { label: "label.userDetailsLastName", value: this.user.lastName },
        { label: "label.userDetailsBirthDate", value: this.user.birthDate },
        { label: "label.phone", value: this.user.phone },
        { label: "label.userDetailsPhoneCountry", value: this.user.phoneIso },
        { label: "label.userDetailsUserType", value: this.user.userType },
        {
          label: "label.userDetailsEmailVerified",
          value: this.user.emailVerifiedAt,
        },
        {
          label: "label.userDetailsPhoneVerified",
          value: this.user.phoneVerifiedAt,
        },
      ];
    },
    accessRows() {
      return [
        {
          channel: "App",
          access: this.user.appAccess,
          login: this.user.appLastLogin,
          attempts: this.user.appAttempts,
        },
        {
          channel: "Portal",
          access: this.user.portalAccess,
          login: this.user.portalLastLogin,
          attempts: this.user.portalAttempts,
        },
      ];
    },
    accessFields() {
      return [
        { key: "channel", label: this.$t("label.userDetailsChannel") },
        { key: "access", label: this.$t("label.userDetailsAccess") },
        { key: "login", label: this.$t("label.userDetailsLastLogin") },
        { key: "attempts", label: this.$t("label.userDetailsAttempts") },
      ];
    },
    companyFields() {
      return [
        { key: "sequence", label: this.$t("label.userDetailsSequence") },
        { key: "company", label: this.$t("module.company") },
        { key: "department", label: this.$t("module.department") },
        { key: "office", label: this.$t("module.office") },
        { key: "position", label: this.$t("module.position") },
      ];
    },
    permissions() {
      return userDetailPermissions(this.user, this.definitions);
    },
    permissionFields() {
      return [
        { key: "name", label: this.$t("label.userDetailsPermission") },
        { key: "level", label: this.$t("label.permissionDefinitionLevel") },
        { key: "definition", label: this.$t("label.name") },
      ];
    },
  },
  watch: {
    userUUID() {
      this.closeEdit();
      this.editVisible = false;
      this.loadUser();
    },
  },
  created() {
    this.loadUser();
  },
  beforeDestroy() {
    this.requestId++;
  },
  methods: {
    openEdit() {
      if (this.loading || !this.user || this.editSaving) return;
      this.editVisible = true;
      this.$nextTick(() => this.$bvModal.show("user-details-edit"));
    },
    closeEdit() {
      this.$bvModal.hide("user-details-edit");
    },
    accessLabel(value) {
      return value === 1
        ? this.$t("label.dashboardAccessible")
        : value === 2
        ? this.$t("label.userDetailsPending")
        : value === 0
        ? this.$t("label.dashboardNoAccess")
        : "-";
    },
    async loadUser() {
      const request = ++this.requestId;
      this.user = null;
      this.loading = true;
      this.error = "";
      this.definitions = [];
      this.definitionsLoading = false;
      this.imageError = false;
      try {
        const result = await userService.show(this.userUUID);
        if (!result?.success || result.data?.uuid !== this.userUUID)
          throw new Error(this.$t("label.userDetailsLoadError"));
        if (request !== this.requestId) return;
        this.user = userDetailsModel(result.data);
        this.loadDefinitions(request);
      } catch (error) {
        if (request === this.requestId)
          this.error =
            sharedHelper.getErrorMessage(error) ||
            this.$t("label.userDetailsLoadError");
      } finally {
        if (request === this.requestId) this.loading = false;
      }
    },
    async loadDefinitions(request) {
      this.definitionsLoading = true;
      try {
        const result = await userService.getPermissionDefinitions();
        if (request === this.requestId)
          this.definitions = userPermissionDefinitionsModel(result);
      } catch (error) {
        /* Permission keys remain readable when definitions are unavailable. */
      } finally {
        if (request === this.requestId) this.definitionsLoading = false;
      }
    },
  },
};
</script>
<style scoped>
.user-detail-avatar {
  width: 100px;
  height: 100px;
  object-fit: cover;
}
.user-detail-initials {
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 32px;
}
.user-superadmin {
  background: #6f42c1;
  color: #fff;
}
</style>
